import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyledTextArea, TextAreaContainer, BottomFixedContainer } from "./styled";
import {
  RiImageLine,
  RiMicLine,
  RiSendPlane2Fill,
  RiStopMiniFill,
  RiTodoLine,
} from "@remixicon/react";
import { Button, UncontrolledTooltip } from "reactstrap";
import AudioRecorder2, { AudioRecorderHandles } from "./AudioRecorderV2";
import { ImageSelector, ImageSelectorHandles } from "./ImageSelector";
import { PromptCatalog } from "../../Modals/PromptCatalog";
import { FileSelector } from "./FileSelector";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { actionSetInputText, selectorInputText } from "../../../redux/chatSlice";

type TextAreaComponentProps = {
  handleSendMessage: (message: string, image?: File) => Promise<void>;
  isBlockedMessage: boolean;
  onNewChatRequest: () => Promise<void>;
};

const PromptCatalogMemo = React.memo(PromptCatalog);

const TextAreaComponent: React.FC<TextAreaComponentProps> = ({
  handleSendMessage,
  isBlockedMessage,
  onNewChatRequest,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [showAudio, setShowAudio] = useState(false);
  const [recording, setRecording] = useState(false);
  const [openCatalog, setOpenCatalog] = useState(false);
  const audioRecorderRef = useRef<AudioRecorderHandles>(null);
  const imageSelectorRef = useRef<ImageSelectorHandles>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [currentMessage, setCurrentMessage] = useState("");
  const [currentImage, setCurrentImage] = useState<File | undefined>(undefined);
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  const inputText = useAppSelector(selectorInputText);
  useEffect(() => {
    if (inputText !== undefined) {
      setCurrentMessage(inputText);
      dispatch(actionSetInputText(undefined));
    }
  }, [inputText]);

  const canSendMessage = currentMessage !== "";

  const onImageUploadSelected = useCallback(() => {
    imageSelectorRef.current?.triggerAction();
  }, []);

  const onPromptCatalogSelected = useCallback(() => {
    setOpenCatalog(true);
  }, []);

  const handleButtonEvent = () => {
    if (canSendMessage) {
      handleSendMessage(currentMessage, currentImage);
      imageSelectorRef.current?.resetComponent();
      setCurrentImage(undefined);
    } else if (recording) {
      setRecording(false);
      audioRecorderRef.current?.triggerAction(); // Chama o método no filho
    } else {
      setShowAudio(true);
      setRecording(true);
    }
  };

  // Send message when Enter is pressed
  const handleEnterPressed = (event: React.KeyboardEvent<Element>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      currentMessage.trim() !== "" &&
      !isProcessingFile
    ) {
      handleSendMessage(currentMessage, currentImage);
      imageSelectorRef.current?.resetComponent();
      setCurrentImage(undefined);
    } else if (event.key === "Enter" && event.shiftKey) {
      if (textAreaRef.current) {
        const cursor_position = textAreaRef.current.selectionStart;
        setCurrentMessage(
          (prevMessage) =>
            prevMessage.slice(0, cursor_position) + "\n" + prevMessage.slice(cursor_position),
        );
        // Move o cursor para a posição correta
        setTimeout(() => {
          textAreaRef.current!.selectionStart = cursor_position + 1;
          textAreaRef.current!.selectionEnd = cursor_position + 1;
        }, 0);
      }
    }
  };

  // Keep track of the state
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if ((e.nativeEvent as unknown as { inputType: string }).inputType === "insertLineBreak") {
      return;
    }
    let text = e.target.value;
    if ((e.nativeEvent as unknown as { inputType: string }).inputType === "insertFromPaste") {
      text = text.trim();
    }
    setCurrentMessage(text);
    adjustTextAreaHeight();
  };

  const adjustTextAreaHeight = () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextAreaHeight();
  }, [currentMessage]);

  return (
    <>
      <PromptCatalogMemo ModalToggle={openCatalog} setModalToggle={setOpenCatalog} />
      <BottomFixedContainer>
        <div className="d-flex align-items-center justify-content-center flex-nowrap gap-2">
          <TextAreaContainer className="textarea">
            <div className="d-flex align-items-center justify-content-center flex-nowrap mx-3">
              <StyledTextArea
                ref={textAreaRef}
                className="textarea"
                id="chat-input"
                onKeyDown={handleEnterPressed}
                rows={1}
                onChange={handleChange}
                disabled={isBlockedMessage || showAudio}
                value={currentMessage}
                autoFocus={true}
                placeholder={`${t("pages.chat.components.inputPlaceHolder")}`}
              />

              {showAudio && (
                <AudioRecorder2
                  ref={audioRecorderRef}
                  onCancel={() => {
                    setShowAudio(false);
                    setRecording(false);
                  }}
                  setSpeechResponse={setCurrentMessage}
                />
              )}
              <Button
                color="light"
                className="btn-icon"
                id="promptCatalogButton"
                onClick={onPromptCatalogSelected}
              >
                <RiTodoLine size={20} style={{ color: "#00A793" }} />
              </Button>
              <UncontrolledTooltip target="promptCatalogButton" placement="left">
                {t("global.chatMenuOptions.promptCatalog")}
              </UncontrolledTooltip>
              <Button
                color="light"
                className="btn-icon"
                id="fileUploadButton"
                onClick={onImageUploadSelected}
              >
                <RiImageLine size={20} style={{ color: "#00A793" }} />
              </Button>
              <UncontrolledTooltip target="fileUploadButton" placement="left">
                {t("global.chatMenuOptions.attachImage")}
              </UncontrolledTooltip>
              <FileSelector onOpen={onNewChatRequest} onLiveUpdating={setIsProcessingFile} />
            </div>

            <ImageSelector ref={imageSelectorRef} setCurrentImage={setCurrentImage} />
          </TextAreaContainer>

          <Button
            style={{ height: "50px", width: "50px" }}
            color="primary"
            className={recording ? "btn-icon btn-soft-danger" : "btn-icon"}
            onClick={handleButtonEvent}
            disabled={(canSendMessage && isProcessingFile) || isBlockedMessage}
          >
            {canSendMessage ? (
              <RiSendPlane2Fill size={20} />
            ) : recording ? (
              <RiStopMiniFill size={20} />
            ) : (
              <RiMicLine size={20} />
            )}
          </Button>
        </div>
      </BottomFixedContainer>
    </>
  );
};

export { TextAreaComponent };
